import {
  useGetHubspotContractFilters,
  useGetStudentStatistics,
  useSearchStudents,
  useSyncHubspotContracts,
} from '@actions';
import { LeftOutlined, RightOutlined } from '@ant-design/icons';
import StudentsTableHeader from '@components/V3/StudentsTableHeader';
import StudentsTableRow from '@components/V3/StudentsTableRow';
import TableColumnSortButtons from '@components/V3/TableColumnSortButtons';
import TableColumns from '@components/V3/TableColumns';
import AgoraSpinner from '@components/V3/Utils/AgoraSpinner';
import { useAuthState } from '@contexts/AuthContext';
import { StudentTier, TriState } from '@shared/clientTypes/misc';
import { GoalStatusTypes, UserRole } from '@shared/enums';
import { Pagination } from 'antd';
import { useState } from 'react';
import { COLORS } from '../../../v3/global';
import DetailsModal from '../components/DetailsModal/DetailsModal';
import './students.styles.scss';
import useToast from '../../../hooks/useToast';
import ContractFilters, { ContractFilterState } from './ContractFilters';
import useExportStudentContacts from './useExportStudentContacts';
import { logger } from '../../../utils/logger';

const Students = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [sortBy, setSortBy] = useState<'firstName' | 'lastName' | 'graduationYear' | undefined>(
    undefined
  );
  const [sortDirectionFirstName, setSortDirectionFirstName] = useState<'asc' | 'desc' | undefined>(
    undefined
  );
  const [sortDirectionLastName, setSortDirectionLastName] = useState<'asc' | 'desc' | undefined>(
    undefined
  );
  const [sortDirectionGraduationYear, setSortDirectionGraduationYear] = useState<
    'asc' | 'desc' | undefined
  >(undefined);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState(undefined);
  const [enrolledIn, setEnrolledIn] = useState(undefined);
  const [mentoredBy, setMentoredBy] = useState(undefined);
  const [graduationYears, setGraduationYears] = useState<string[]>([]);
  const [studentTiers, setStudentTiers] = useState<StudentTier[]>([]);
  const [applicationStages, setApplicationStages] = useState<GoalStatusTypes[]>([]);
  const [universities, setUniversities] = useState<string[]>([]);
  const [majors, setMajors] = useState<string[]>([]);
  const [extraFilter, setExtraFilter] = useState(undefined);
  const [isTestUser, setIsTestUser] = useState<boolean | undefined>(false);
  const [isDeactivated, setIsDeactivated] = useState<boolean | undefined>(false);
  const [hasCoachMe, setHasCoachMe] = useState<boolean | undefined>(undefined);
  const [contractFilter, setContractFilter] = useState<ContractFilterState>({});

  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [studentId, setStudentId] = useState<string>();

  const searchParams = {
    search: search,
    page: currentPage,
    limit: pageSize,
    sortBy: sortBy,
    sortDirection:
      sortBy === 'firstName'
        ? sortDirectionFirstName
        : sortBy === 'lastName'
        ? sortDirectionLastName
        : sortBy === 'graduationYear'
        ? sortDirectionGraduationYear
        : undefined,
    status: status,
    enrolledIn: enrolledIn,
    mentoredBy: mentoredBy,
    graduationYears: graduationYears,
    tiers: studentTiers,
    applicationStages: applicationStages,
    universities: universities,
    majors: majors,
    extraFilter: extraFilter,
    isTestUser: isTestUser,
    isDeactivated: isDeactivated,
    hasCoachMe: hasCoachMe,
    hasContract: contractFilter.hasContract,
    contractStage: contractFilter.stage,
    contractProperty: contractFilter.property,
    contractValue: contractFilter.value,
  };

  const {
    data: studentsData,
    isLoading: isLoadingSearchStudents,
    refetch: refetchStudents,
  } = useSearchStudents(searchParams);

  const { roles } = useAuthState();
  const canExport = roles.includes(UserRole.Financial) || roles.includes(UserRole.SuperUser);
  const toast = useToast();

  const {
    data: contractFilters,
    isLoading: isLoadingContractFilters,
    refetch: refetchContractFilters,
  } = useGetHubspotContractFilters();

  const { mutate: syncHubspotContracts, isLoading: isSyncingContracts } = useSyncHubspotContracts({
    onSuccess: (summary) => {
      toast.success(
        `HubSpot sync done: ${summary.matched} students matched from ${
          summary.dealsInPipeline
        } deals in "${summary.pipelineLabel}"${
          summary.unmatchedTotal ? `, ${summary.unmatchedTotal} deals without a student` : ''
        }.`
      );
      refetchContractFilters();
      refetchStudents();
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.error?.message ?? 'HubSpot sync failed.');
    },
  });

  const contractFilterChangeHandler = (patch: Partial<ContractFilterState>) => {
    setContractFilter((current) => ({ ...current, ...patch }));
    setCurrentPage(1);
  };

  const { exportStudentContacts, isExporting } = useExportStudentContacts(
    searchParams,
    studentsData?.total ?? 0
  );

  const { data: studentStatistics, isLoading: isLoadingStudentStatistics } =
    useGetStudentStatistics();

  const clearExtraFilter = () => setExtraFilter(undefined);

  const clearFilters = () => {
    setSearch('');
    setStatus(undefined);
    setEnrolledIn(undefined);
    setMentoredBy(undefined);
    setGraduationYears([]);
    setStudentTiers([]);
    setApplicationStages([]);
    setUniversities([]);
    setMajors([]);
  };

  const handleSearch = (search: string) => {
    setSearch(search);
    setCurrentPage(1);
    clearExtraFilter();
  };

  const handleFilterBy = (status: any) => {
    setStatus(status);
    setCurrentPage(1);
    clearExtraFilter();
  };

  const handleEnrolledIn = (course: any) => {
    setEnrolledIn(course);
    setCurrentPage(1);
    clearExtraFilter();
  };

  const handleMentoredBy = (mentor: any) => {
    setMentoredBy(mentor);
    setCurrentPage(1);
    clearExtraFilter();
  };

  const handleExtraFilter = (extraFilter: any) => {
    setExtraFilter(extraFilter);
    setCurrentPage(1);
    clearFilters();
  };

  const closeSessionModalHandler = () => {
    setStudentId(undefined);
    setShowDetailsModal(false);
  };

  const profileClickHandler = (studentId: string) => {
    setStudentId(studentId);
    setShowDetailsModal(true);
  };

  const indeterminateHandler = (state?: boolean) => {
    let newState;
    switch (state) {
      case false:
        newState = undefined;
        break;

      case undefined:
        newState = true;
        break;

      case true:
        newState = false;
        break;
    }
    return newState;
  };

  const isTestUserHandler = (value: TriState) => {
    setIsTestUser(value === 'indeterminate' ? undefined : value === 'checked');
    setCurrentPage(1);
  };

  const isDeactivatedHandler = (value: TriState) => {
    setIsDeactivated(value === 'indeterminate' ? undefined : value === 'checked');
    setCurrentPage(1);
  };

  const hasCoachMeHandler = (value: TriState) => {
    setHasCoachMe(value === 'indeterminate' ? undefined : value === 'checked');
    setCurrentPage(1);
  };

  const handleAddGraduationYear = (year: string) => {
    setGraduationYears((prevYears) => [...prevYears, year]);
    setCurrentPage(1);
    clearExtraFilter();
  };

  const handleRemoveGraduationYear = (year: string) => {
    setGraduationYears((prevYears) => prevYears?.filter((item) => item !== year));
    setCurrentPage(1);
    clearExtraFilter();
  };

  const handleClearGraduationYears = () => {
    setGraduationYears([]);
    setCurrentPage(1);
    clearExtraFilter();
  };

  const handleAddTier = (tier: StudentTier) => {
    setStudentTiers((prevTiers) => (prevTiers.includes(tier) ? prevTiers : [...prevTiers, tier]));
    setCurrentPage(1);
    clearExtraFilter();
  };

  const handleRemoveTier = (tier: StudentTier) => {
    setStudentTiers((prevTiers) => prevTiers?.filter((item) => item !== tier));
    setCurrentPage(1);
    clearExtraFilter();
  };

  const handleClearTiers = () => {
    setStudentTiers([]);
    setCurrentPage(1);
    clearExtraFilter();
  };

  const handleAddApplicationStage = (stage: GoalStatusTypes) => {
    setApplicationStages((prevStages) =>
      prevStages.includes(stage) ? prevStages : [...prevStages, stage]
    );
    setCurrentPage(1);
    clearExtraFilter();
  };

  const handleRemoveApplicationStage = (stage: GoalStatusTypes) => {
    setApplicationStages((prevStages) => prevStages?.filter((item) => item !== stage));
    setCurrentPage(1);
    clearExtraFilter();
  };

  const handleClearApplicationStages = () => {
    setApplicationStages([]);
    setCurrentPage(1);
    clearExtraFilter();
  };

  const handleAddUniversity = (university: string) => {
    setUniversities((prevUniversities) =>
      prevUniversities.includes(university) ? prevUniversities : [...prevUniversities, university]
    );
    setCurrentPage(1);
    clearExtraFilter();
  };

  const handleRemoveUniversity = (university: string) => {
    setUniversities((prevUniversities) => prevUniversities?.filter((item) => item !== university));
    setCurrentPage(1);
    clearExtraFilter();
  };

  const handleClearUniversities = () => {
    setUniversities([]);
    setCurrentPage(1);
    clearExtraFilter();
  };

  const handleAddMajor = (major: string) => {
    setMajors((prevMajors) => (prevMajors.includes(major) ? prevMajors : [...prevMajors, major]));
    setCurrentPage(1);
    clearExtraFilter();
  };

  const handleRemoveMajor = (major: string) => {
    setMajors((prevMajors) => prevMajors?.filter((item) => item !== major));
    setCurrentPage(1);
    clearExtraFilter();
  };

  const handleClearMajors = () => {
    setMajors([]);
    setCurrentPage(1);
    clearExtraFilter();
  };

  return (
    <div
      style={{
        backgroundColor: COLORS.SECTION_BACKGROUND,
        color: COLORS.TEXT_PRIMARY,
        minHeight: 'calc(100vh - 74px)',
      }}
    >
      <StudentsTableHeader
        studentStatistics={studentStatistics}
        handleSearch={handleSearch}
        handleFilterBy={handleFilterBy}
        handleEnrolledIn={handleEnrolledIn}
        handleMentoredBy={handleMentoredBy}
        handleAddGraduationYear={handleAddGraduationYear}
        handleRemoveGraduationYear={handleRemoveGraduationYear}
        handleClearGraduationYears={handleClearGraduationYears}
        tiers={studentTiers}
        handleAddTier={handleAddTier}
        handleRemoveTier={handleRemoveTier}
        handleClearTiers={handleClearTiers}
        applicationStages={applicationStages}
        handleAddApplicationStage={handleAddApplicationStage}
        handleRemoveApplicationStage={handleRemoveApplicationStage}
        handleClearApplicationStages={handleClearApplicationStages}
        universities={universities}
        handleAddUniversity={handleAddUniversity}
        handleRemoveUniversity={handleRemoveUniversity}
        handleClearUniversities={handleClearUniversities}
        majors={majors}
        handleAddMajor={handleAddMajor}
        handleRemoveMajor={handleRemoveMajor}
        handleClearMajors={handleClearMajors}
        handleExtraFilter={handleExtraFilter}
        extraFilter={extraFilter}
        isLoadingStudentStatistics={isLoadingStudentStatistics}
        isTestUserHandler={isTestUserHandler}
        hasCoachMe={hasCoachMe}
        hasCoachMeHandler={hasCoachMeHandler}
        onSyncContracts={canExport ? () => syncHubspotContracts({}) : undefined}
        isSyncingContracts={isSyncingContracts}
        extraFilters={
          <ContractFilters
            filters={contractFilters}
            isLoading={isLoadingContractFilters}
            state={contractFilter}
            onChange={contractFilterChangeHandler}
          />
        }
        onExport={canExport ? exportStudentContacts : undefined}
        isExporting={isExporting}
        isDeactivatedHandler={isDeactivatedHandler}
        isTestUser={isTestUser}
        isDeactivated={isDeactivated}
      />

      {showDetailsModal && (
        <DetailsModal
          studentId={studentId}
          page="student"
          isOpen={true}
          onClose={closeSessionModalHandler}
        />
      )}

      <div>
        <div className="table-container">
          {/*TABLE COLUMNS*/}
          <div style={{ marginBottom: '16px' }}>
            <TableColumns>
              <div className="table-columns-container">
                <div
                  className="table-filter-columns-container"
                  style={{ width: '14%', marginRight: '1%' }}
                  onClick={() => {
                    setSortBy('firstName');
                    setSortDirectionLastName(undefined);
                    setSortDirectionGraduationYear(undefined);
                    if (sortDirectionFirstName === 'asc') {
                      setSortDirectionFirstName('desc');
                    } else if (sortDirectionFirstName === 'desc') {
                      setSortDirectionFirstName(undefined);
                      setSortBy(undefined);
                    } else {
                      setSortDirectionFirstName('asc');
                    }
                  }}
                >
                  <span style={{ marginRight: '8px' }}>First Name</span>
                  <TableColumnSortButtons sortType={sortDirectionFirstName} />
                </div>

                <div
                  className="table-filter-columns-container"
                  style={{ width: '14%', marginRight: '1%' }}
                  onClick={() => {
                    setSortBy('lastName');
                    setSortDirectionGraduationYear(undefined);
                    setSortDirectionFirstName(undefined);
                    if (sortDirectionLastName === 'asc') {
                      setSortDirectionLastName('desc');
                    } else if (sortDirectionLastName === 'desc') {
                      setSortDirectionLastName(undefined);
                      setSortBy(undefined);
                    } else {
                      setSortDirectionLastName('asc');
                    }
                  }}
                >
                  <span style={{ marginRight: '8px' }}>Last Name</span>
                  <TableColumnSortButtons sortType={sortDirectionLastName} />
                </div>
                <div style={{ width: '25%', marginRight: '1%' }}>
                  <span>Enrolled In</span>
                </div>
                <div style={{ width: '10%', marginRight: '1%' }}>
                  <span>Contract</span>
                </div>
                <div
                  className="table-filter-columns-container"
                  style={{ width: '19%', marginRight: '1%' }}
                  onClick={() => {
                    setSortBy('graduationYear');
                    setSortDirectionFirstName(undefined);
                    setSortDirectionLastName(undefined);
                    if (sortDirectionGraduationYear === 'asc') {
                      setSortDirectionGraduationYear('desc');
                    } else if (sortDirectionGraduationYear === 'desc') {
                      setSortDirectionGraduationYear(undefined);
                      setSortBy(undefined);
                    } else {
                      setSortDirectionGraduationYear('asc');
                    }
                  }}
                >
                  <span style={{ marginRight: '8px' }}>Graduation</span>
                  <TableColumnSortButtons sortType={sortDirectionGraduationYear} />
                </div>

                <div style={{ width: '24%', marginRight: '1%' }}>
                  <span>Actions</span>
                </div>
              </div>
            </TableColumns>
          </div>

          {/*TABLE ROWS*/}
          {!isLoadingSearchStudents ? (
            studentsData?.students?.map((item) => (
              <StudentsTableRow
                currentUserId={item?._id}
                key={item?._id}
                studentData={item}
                refetchSearchStudents={refetchStudents}
                onProfileClick={profileClickHandler}
              />
            ))
          ) : (
            <AgoraSpinner fontSize={32} margin="32px" />
          )}
        </div>

        {/*PAGINATION*/}
        <div
          style={{
            width: '100%',
            padding: '24px 24px 120px 24px',
            display: 'flex',
            justifyContent: 'space-between',
          }}
        >
          <div>
            <span
              style={{ color: COLORS.TEXT_SECONDARY, marginRight: '8px' }}
              className="flags-table-pagination-label"
            >
              Rows per page
            </span>
            <select
              onChange={(e) => {
                const selectedValue = e.target.value;
                setPageSize(Number(selectedValue));
              }}
              className="mentors-row-custom-select-component pagination-select-container"
              style={{
                borderColor: COLORS.TEXT_PRIMARY,
                color: COLORS.TEXT_PRIMARY,
                backgroundColor: COLORS.SECTION_BACKGROUND,
              }}
              defaultValue={10}
            >
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
              <option value={studentsData?.total}>All</option>
            </select>
          </div>

          <div style={{ color: COLORS.TEXT_SECONDARY }} className="mentors-table-pagination-label">
            {`${(currentPage - 1) * pageSize + 1}-${Math.min(
              currentPage * pageSize,
              studentsData?.total || 0
            )} of ${studentsData?.total || 0}`}
          </div>

          {/*PAGINATION NAVIGATOR*/}
          <div>
            <Pagination
              className="custom-pagination"
              showSizeChanger={false}
              current={currentPage}
              defaultCurrent={1}
              total={studentsData?.total}
              pageSize={pageSize}
              onChange={(page) => setCurrentPage(page)}
              prevIcon={
                <div
                  style={{
                    height: '100%',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                  }}
                >
                  <LeftOutlined
                    style={{
                      color: COLORS.TEXT_SECONDARY,
                    }}
                  />
                </div>
              }
              nextIcon={
                <div
                  style={{
                    height: '100%',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                  }}
                >
                  <RightOutlined
                    style={{
                      color: COLORS.TEXT_SECONDARY,
                    }}
                  />
                </div>
              }
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Students;
